import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../shared/Icon';
import type {
  HorizonHeadline,
  HorizonOutletRail,
  HorizonOutletSignal,
  HorizonTile,
} from './horizonHighlights';

/**
 * Horizon's highlights block. It deliberately reuses Performance's `perf-hero`
 * DOM and classes so the two tabs read as one product rather than two.
 */
function HighlightTile({ tile }: { tile: HorizonTile }) {
  const { t } = useTranslation('horizon');
  const [flipped, setFlipped] = useState(false);

  const toggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setFlipped(f => !f);
  };

  return (
    <div
      className={`ph-metric-flip ${tile.tone}${flipped ? ' is-flipped' : ''}`}
      data-hint={t('clickMore')}
      onClick={toggle}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') toggle(e);
      }}
      role="button"
      tabIndex={0}
      aria-pressed={flipped}
      aria-label={`${tile.label}: ${tile.value}. ${flipped ? t('hideDetails') : t('showDetails')}.`}
    >
      <div className="ph-metric-flip-inner">
        <div className="ph-metric-face ph-metric-front">
          <span className="ph-metric-ic">
            <Icon name={tile.icon} size={13} />
          </span>
          <span className="ph-metric-lab">{tile.label}</span>
          <span className="ph-metric-val">{tile.value}</span>
        </div>
        <div className="ph-metric-face ph-metric-back">
          <div className="ph-metric-back-scroll">
            <p className="ph-metric-detail">{tile.detail}</p>
            {tile.action ? <p className="ph-metric-action">{tile.action}</p> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function OutletRow({
  signal,
  onFocus,
}: {
  signal: HorizonOutletSignal;
  onFocus?: (outletId: string) => void;
}) {
  const { t } = useTranslation('horizon');
  const clickable = !!onFocus;
  const activate = () => onFocus?.(signal.outletId);

  return (
    <div
      className={`ph-outlet ${signal.tone}${clickable ? ' is-clickable' : ''}`}
      title={clickable ? t('openOutlet', { name: signal.outletName }) : undefined}
      onClick={clickable ? activate : undefined}
      onKeyDown={
        clickable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activate();
              }
            }
          : undefined
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <span className="ph-outlet-ic" aria-hidden>
        <Icon name={signal.icon} size={13} />
      </span>
      <div className="ph-outlet-copy">
        <div className="ph-outlet-lab">{signal.outletName}</div>
        <div className="ph-outlet-detail">{signal.detail}</div>
      </div>
      <div className="ph-outlet-val">{signal.value}</div>
    </div>
  );
}

export function HorizonHero({
  headline,
  tiles,
  rail,
  onFocusOutlet,
  toolbar,
}: {
  headline: HorizonHeadline;
  tiles: HorizonTile[];
  /** Omitted when a single outlet is in focus — there is nothing to compare. */
  rail?: HorizonOutletRail | null;
  onFocusOutlet?: (outletId: string) => void;
  toolbar?: ReactNode;
}) {
  const { t } = useTranslation('horizon');
  const showRail = !!rail && (rail.first.length > 0 || rail.fullest.length > 0);

  return (
    <div className="perf-hero hz-hero" data-ftue="horizon-hero">
      <div className="ph-head hz-hero-head">
        <div className="hz-hero-copy">
          <h2 className="ph-title">{headline.headline}</h2>
          {headline.subline && (
            <p className="ph-agent-sub">{headline.subline}</p>
          )}
        </div>
        {toolbar}
      </div>

      {(showRail || tiles.length > 0) && (
        <div className={`ph-body${showRail ? ' has-rail' : ' metrics-only'}`}>
          {showRail && rail && (
            <aside className="ph-rail" aria-label={t('outletsFirst')}>
              <div className="ph-board">
                {rail.first.length > 0 && (
                  <div className="ph-group">
                    <div
                      className={`ph-group-lab ${rail.firstLabel === t('needsAttention') ? 'risk' : ''}`}
                    >
                      <Icon
                        name={
                          rail.firstLabel === t('needsAttention') ? 'flag' : 'arrowDown'
                        }
                        size={13}
                      />
                      {rail.firstLabel}
                      {rail.firstTotal > rail.first.length
                        ? ` · ${rail.firstTotal}`
                        : ''}
                    </div>
                    <div className="ph-cards">
                      {rail.first.map(signal => (
                        <OutletRow
                          key={`first-${signal.outletId}`}
                          signal={signal}
                          onFocus={onFocusOutlet}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {rail.fullest.length > 0 && (
                  <div className="ph-group">
                    <div className="ph-group-lab win">
                      <Icon name="trendUp" size={13} />
                      {t('fillingFastest')}
                    </div>
                    <div className="ph-cards">
                      {rail.fullest.map(signal => (
                        <OutletRow
                          key={`full-${signal.outletId}`}
                          signal={signal}
                          onFocus={onFocusOutlet}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          )}

          <div className="ph-metrics-pane">
            <div className="ph-group">
              <div className="ph-group-lab">
                <Icon name="activity" size={13} />
                {t('highlightsLabel')}
              </div>
              <div className="ph-metrics ph-metrics-band">
                {tiles.map(tile => (
                  <HighlightTile key={tile.key} tile={tile} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
